import React, { useState } from 'react';
import { serializeServices } from '../hooks/useSystemStatus';

const statusIcon = (status) => {
    if (status === 'green') return { symbol: '●', color: '#22c55e' };
    if (status === 'yellow') return { symbol: '●', color: '#f59e0b' };
    if (status === 'red') return { symbol: '●', color: '#ef4444' };
    return { symbol: '●', color: '#9ca3af' };
};

function buildDiagnosticText(services, lastCheckedAt) {
    const lines = [
        `SamSurveyor System Diagnostic`,
        `Checked: ${lastCheckedAt ? lastCheckedAt.toISOString() : 'N/A'}`,
        `User Agent: ${navigator.userAgent}`,
        `Console URL: ${window.location.href}`,
        '',
        'Service          | Status  | Latency | Summary',
        '-----------------|---------|---------|--------',
    ];
    for (const svc of services) {
        const name = svc.label.padEnd(17);
        const st = (svc.status || 'unknown').padEnd(7);
        const lat = svc.latencyMs != null ? `${svc.latencyMs}ms`.padEnd(7) : '—'.padEnd(7);
        const sum = svc.summary || '';
        lines.push(`${name}| ${st} | ${lat} | ${sum}`);
    }

    return lines.join('\n');
}

/** The structured packet sent to the backend when support is alerted. */
function buildAlertPacket(services, aggregateStatus, lastCheckedAt) {
    const errorCodes = Array.from(new Set(services.map(s => s.errorCode).filter(Boolean)));

    return {
        trigger: 'manual',
        aggregate_status: aggregateStatus,
        services: serializeServices(services),
        error_codes: errorCodes,
        client_errors: services.filter(s => s.isSelf && s.status !== 'green').map(s => s.summary),
        url: window.location.href,
        user_agent: navigator.userAgent,
        last_checked_at: lastCheckedAt ? lastCheckedAt.toISOString() : null,
    };
}

export function SystemStatusModal({ services, aggregateStatus, lastCheckedAt, onRefresh, onClose }) {
    const diagText = buildDiagnosticText(services, lastCheckedAt);
    const [alertState, setAlertState] = useState({ phase: 'idle' });

    const handleAlertSupport = async () => {
        setAlertState({ phase: 'sending' });
        try {
            const resp = await fetch('/api/console/system/support-alert', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(buildAlertPacket(services, aggregateStatus, lastCheckedAt)),
            });
            const body = await resp.json().catch(() => ({}));
            if (body.delivery === 'email') {
                setAlertState({ phase: 'sent', alertId: body.alert_id, archived: body.archived });
            } else if (body.archived) {
                setAlertState({ phase: 'stored', alertId: body.alert_id, errors: body.errors || [] });
            } else {
                setAlertState({ phase: 'failed', alertId: body.alert_id, errors: body.errors || [] });
            }
        } catch (err) {
            setAlertState({ phase: 'failed', errors: [{ stage: 'client', code: 'ALERT_REQUEST_FAILED', detail: err.message }] });
        }
    };

    const copyDiagnostic = () => {
        navigator.clipboard?.writeText(diagText).catch(() => { });
    };

    const alertBanner = () => {
        if (alertState.phase === 'sent') {
            return {
                bg: '#ecfdf5', border: '#a7f3d0', color: '#065f46',
                text: `Support alerted by email${alertState.archived ? ' and filed to the audit store' : ''}. Reference ${alertState.alertId}.`,
            };
        }
        if (alertState.phase === 'stored') {
            return {
                bg: '#fffbeb', border: '#fde68a', color: '#92400e',
                text: `Email delivery failed (${alertState.errors?.[0]?.code || 'unknown'}). The status packet was filed for support instead — reference ${alertState.alertId}.`,
            };
        }
        if (alertState.phase === 'failed') {
            return {
                bg: '#fef2f2', border: '#fecaca', color: '#991b1b',
                text: `Could not raise the alert (${alertState.errors?.map(e => e.code).join(', ') || 'unknown'}). Copy the diagnostic and send it manually.`,
            };
        }
        return null;
    };
    const banner = alertBanner();

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)',
        }} onClick={onClose}>
            <div style={{
                background: '#fff', borderRadius: 12, padding: 0, minWidth: 520, maxWidth: 640,
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
            }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{
                    padding: '16px 20px', borderBottom: '1px solid #E8EAED',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'var(--color-bg-base)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 18, color: statusIcon(aggregateStatus).color }}>
                            {aggregateStatus === 'green' ? '✓' : aggregateStatus === 'yellow' ? '⚠' : '✕'}
                        </span>
                        <span style={{ fontWeight: 700, fontSize: 16 }}>System Status</span>
                        {lastCheckedAt && (
                            <span style={{ color: '#5F6368', fontSize: 12 }}>
                                {lastCheckedAt.toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', fontSize: 20, cursor: 'pointer',
                        color: '#5F6368', padding: '2px 6px', lineHeight: 1,
                    }}>×</button>
                </div>

                {/* Service Table */}
                <div style={{ padding: '12px 20px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #E8EAED' }}>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Service</th>
                                <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, width: 60 }}>Status</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, width: 70 }}>Latency</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Summary</th>
                            </tr>
                        </thead>
                        <tbody>
                            {services.map(svc => {
                                const icon = statusIcon(svc.status);
                                return (
                                    <tr key={svc.key} style={{ borderBottom: '1px solid #F5F6F7' }}>
                                        <td style={{ padding: '8px', fontWeight: 500 }}>{svc.label}</td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>
                                            <span style={{ color: icon.color, fontSize: 14 }}>{icon.symbol}</span>
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: '#5F6368', fontSize: 12 }}>
                                            {svc.latencyMs != null ? `${svc.latencyMs}ms` : '—'}
                                        </td>
                                        <td style={{ padding: '8px', color: '#5F6368', fontSize: 12 }}>{svc.summary || ''}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {banner && (
                    <div style={{
                        margin: '0 20px 12px', padding: '10px 12px', borderRadius: 6,
                        background: banner.bg, border: `1px solid ${banner.border}`,
                        color: banner.color, fontSize: 12, lineHeight: 1.5,
                    }}>
                        {banner.text}
                        {alertState.phase === 'failed' && (
                            <button onClick={copyDiagnostic} style={{
                                marginLeft: 8, background: 'transparent', border: `1px solid ${banner.border}`,
                                borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: banner.color,
                            }}>Copy diagnostic</button>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div style={{
                    padding: '12px 20px', borderTop: '1px solid #E8EAED',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#FAFBFC',
                }}>
                    <button onClick={onRefresh} style={{
                        background: 'transparent', border: '1px solid #E8EAED', borderRadius: 6,
                        padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#5F6368',
                        transition: 'all 0.15s',
                    }}>
                        ↻ Refresh
                    </button>
                    <button
                        onClick={handleAlertSupport}
                        disabled={alertState.phase === 'sending'}
                        style={{
                            background: alertState.phase === 'sending' ? '#f87171' : '#ef4444', border: 'none', borderRadius: 6,
                            padding: '8px 18px', cursor: alertState.phase === 'sending' ? 'wait' : 'pointer',
                            fontSize: 13, color: '#fff', fontWeight: 600, transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { if (alertState.phase !== 'sending') e.target.style.background = '#dc2626'; }}
                        onMouseLeave={e => { if (alertState.phase !== 'sending') e.target.style.background = '#ef4444'; }}
                    >
                        {alertState.phase === 'sending' ? 'Alerting…' : 'Alert System Support'}
                    </button>
                </div>
            </div>
        </div>
    );
}
